import { Blocs } from '@business/blocs';
import { OrgId, OrgResponse } from '@business/entities/organization';
import { CommonService } from '@business/services';
import { BaseBloc } from '@core/utils/bloc';
import { resolve } from '@core/utils/ioc';
import { map, tap } from 'rxjs/operators';
import { forkJoin, of, BehaviorSubject } from 'rxjs';
import { CompactFileFolderResponse } from '@business/entities/file';
import { FormAPI } from '@business/api/form';
import { LoadingState } from '@core/utils/repository/loading_state';
import { PaginatedController } from '@core/utils/paginated';
import { EstimateSearchParams, CompactTMEstimateResponse } from '@business/entities/tm';
import { EstimatorTemplateType } from '@business/entities/tm/TmTemplateEstimatorResponse';

export const ESTIMATES_INITIAL_QUERY: EstimateSearchParams = {
  page: 1,
  search: '',
  sortBy: 'created_at',
  sortDirection: 'desc',
  withTrashed: true,
  type: EstimatorTemplateType.main,
};

export class EstimatesPageBloc extends BaseBloc {
  private readonly organizationBloc = resolve(Blocs.organization);
  private readonly estimateTemplateBloc = resolve(Blocs.tmEstimatorTempate);
  private readonly estimateBloc = resolve(Blocs.estimateV2);

  constructor(public readonly orgId?: OrgId) {
    super();
  }

  $organizations = new BehaviorSubject<OrgResponse[] | any[]>([]);
  readonly fileForms$ = new BehaviorSubject<CompactFileFolderResponse[]>([]);

  loading = new LoadingState();

  searchOrganizations = (search: string) => {
    return this.organizationBloc.searchOrganizations(search).pipe(
      map(data => {
        this.$organizations.next(data.data);
        return CommonService.selectItems(data.data, 'name', 'id');
      }),
    );
  };

  fetchEstimates = (query?: EstimateSearchParams) => {
    return this.estimateBloc.fetchEstimatesPaginated({ ...query, orgId: this.orgId }).pipe(this.loading.run({}));
  };

  onInit() {
    return this.paginated.fetch().pipe(this.loading.run());
  }

  readonly paginated = new PaginatedController<CompactTMEstimateResponse, string, EstimateSearchParams>(
    this.fetchEstimates,
    '',
    ESTIMATES_INITIAL_QUERY,
  );
  readonly estimates$ = this.paginated.items$;
  readonly query$ = this.paginated.pageQuery$;

  getFileForms(orgId: OrgId) {
    FormAPI.getOrgFileForms(orgId)
      .pipe(tap(forms => this.fileForms$.next(forms)))
      .subscribe();
  }

  createEstimate = this.estimateBloc.createEstimate;
  deleteEstimate = this.estimateBloc.deleteEstimate;
  restoreEstimate = this.estimateBloc.restoreEstimate;
}
